import { requirePlanId, type PlanId } from "@/features/billing/plans"

export const APP_BILLING_LIFECYCLE = {
  FREE: "free",
  ACTIVE_PAID: "active_paid",
  PAST_DUE: "past_due",
  CANCELING_UNTIL_PERIOD_END: "canceling_until_period_end",
  EXPIRED_RESTRICTED: "expired_restricted",
} as const

export type AppBillingLifecycleState =
  (typeof APP_BILLING_LIFECYCLE)[keyof typeof APP_BILLING_LIFECYCLE]

export type UserBillingLifecycleSource = {
  currentPlan: string
  subscriptionStatus: string
  cancelAtPeriodEnd: boolean
}

export function deriveAppBillingLifecycleState(
  row: UserBillingLifecycleSource,
): AppBillingLifecycleState {
  const plan: PlanId = requirePlanId(row.currentPlan)
  const status = (row.subscriptionStatus ?? "").trim().toLowerCase()
  const cancelAtEnd = Boolean(row.cancelAtPeriodEnd)

  if (status === "past_due") return APP_BILLING_LIFECYCLE.PAST_DUE

  if (cancelAtEnd && (status === "active" || status === "trialing")) {
    return APP_BILLING_LIFECYCLE.CANCELING_UNTIL_PERIOD_END
  }

  const accessActive = status === "active" || status === "trialing"
  if (accessActive && plan !== "free") {
    return APP_BILLING_LIFECYCLE.ACTIVE_PAID
  }

  if (plan === "free") return APP_BILLING_LIFECYCLE.FREE

  return APP_BILLING_LIFECYCLE.EXPIRED_RESTRICTED
}

export function lifecycleAllowsPaidCatalogEntitlements(
  state: AppBillingLifecycleState,
): boolean {
  return (
    state === APP_BILLING_LIFECYCLE.ACTIVE_PAID ||
    state === APP_BILLING_LIFECYCLE.CANCELING_UNTIL_PERIOD_END
  )
}

export function lifecycleUsesRestrictedEntitlements(
  state: AppBillingLifecycleState,
): boolean {
  return !lifecycleAllowsPaidCatalogEntitlements(state)
}

export function billingLifecycleWantsNewSubscriptionCheckout(
  state: AppBillingLifecycleState,
): boolean {
  return (
    state === APP_BILLING_LIFECYCLE.FREE ||
    state === APP_BILLING_LIFECYCLE.EXPIRED_RESTRICTED
  )
}
